import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { useDispatchWrapper } from "../../../../../../hooks";
import { showMessage } from "../../../../../../redux/messages/messagesActions";
import {
  fetchCommAutoChecklistSavedStatusAsync,
  saveCommAutoChecklistResponseAsync,
} from "../../../../../../redux/commAutoPolicy/commAutoMarketingHistory/commAutoMarketingHistoryActions";
import { ITemplateField } from "../../../../../../redux/CustomFormFields/CustomFormFields.types";
import { DynamicFormRenderer } from "../../../../../CustomFormFields/DynamicFormRenderer";

interface ICommAutoChecklistTabProps {
  customerId: number | string;
  customerPolicyId: number | string;
  onSaveSuccess: () => void;
  /**
   * Feature: lead-commercial-auto-risk-profile
   *
   * Optional lead-scope key. Additive and optional so the customer-policy
   * behavior is unchanged when it is not supplied (Requirements 16.1, 16.3).
   * When a non-empty `leadId` is supplied the checklist status fetch and save
   * are keyed by `lead_id` instead of `customer_policy_id`
   * (Requirements 12.3, 5.2, 5.4).
   */
  leadId?: string;
}

export const CommAutoChecklistTab: React.FC<ICommAutoChecklistTabProps> = (
  props
) => {
  const { customerId, customerPolicyId, onSaveSuccess, leadId } = props;

  const dispatch = useDispatchWrapper();

  // Local fields state — mirrors the step 9 pattern.
  // null  → no saved data yet; DynamicFormRenderer will self-fetch the template.
  // array → saved data from get-policy-binder-checklist; shown directly.
  const [fields, setFields] = React.useState<ITemplateField[] | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // In lead scope the checklist is keyed by `lead_id`, so a present `leadId`
    // is a valid key even when `customerPolicyId` is absent.
    if (!customerPolicyId && !leadId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    dispatch(
      fetchCommAutoChecklistSavedStatusAsync(
        customerPolicyId,
        (savedFields) => {
          // savedFields is the checklist array when data exists, null otherwise.
          setFields(savedFields);
          setLoading(false);
        },
        leadId
      )
    );
  }, [customerPolicyId, leadId]);

  const handleSubmit = (updatedFields: ITemplateField[]) => {
    dispatch(
      saveCommAutoChecklistResponseAsync(
        customerId,
        customerPolicyId,
        updatedFields,
        () => {
          // After save, re-fetch from get-policy-binder-checklist so the form
          // shows the persisted values (not the empty questionnaire template).
          dispatch(
            fetchCommAutoChecklistSavedStatusAsync(
              customerPolicyId,
              (savedFields) => {
                setFields(savedFields);
              },
              leadId
            )
          );
          onSaveSuccess();
          dispatch(
            showMessage({
              type: "success",
              message: "Checklist saved successfully!",
              displayAs: "snackbar",
            })
          );
        },
        (message: string) => {
          dispatch(
            showMessage({
              type: "error",
              message,
              displayAs: "snackbar",
            })
          );
        },
        leadId
      )
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DynamicFormRenderer
      templateCode="IZIQNY"
      fields={fields}
      onSubmit={handleSubmit}
    />
  );
};
